import { normalizeText } from "@/lib/geoText";

export type SuggestionKind = "miasto" | "województwo" | "schronisko";

export interface SuggestionCandidate {
  label: string;
  kind: SuggestionKind;
}

export interface Suggestion {
  label: string;
  kind: SuggestionKind;
  distance: number;
}

// Simple Levenshtein distance
export const levenshtein = (a: string, b: string): number => {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  const m = a.length, n = b.length;
  const dp: number[] = new Array(n + 1);
  for (let j = 0; j <= n; j++) dp[j] = j;
  for (let i = 1; i <= m; i++) {
    let prev = dp[0];
    dp[0] = i;
    for (let j = 1; j <= n; j++) {
      const tmp = dp[j];
      dp[j] = a[i - 1] === b[j - 1]
        ? prev
        : 1 + Math.min(prev, dp[j], dp[j - 1]);
      prev = tmp;
    }
  }
  return dp[n];
};

/**
 * For a fragment query like "tarnó" or "kuż", return up to `limit` closest
 * suggestions from `candidates`. Compares the needle to each word AND to a
 * prefix of the word with the same length as the needle, so partial typing
 * matches the start of longer words like "Tarnów" or "Kuźnice".
 */
export const findSuggestions = (
  query: string,
  candidates: SuggestionCandidate[],
  limit = 3,
): Suggestion[] => {
  const trimmed = (query || "").trim();
  if (!trimmed || trimmed.length < 3) return [];
  const needle = normalizeText(trimmed);
  if (!needle || needle.length < 3) return [];
  // Tylko gdy zapytanie wygląda jak fragment nazwy (litery, opc. spacja/myślnik).
  // Cyfry, znaki specjalne, symbole — pomijamy, żeby nie podpowiadać losowo.
  if (!/^[\p{L}][\p{L}\s-]*$/u.test(needle)) return [];

  const best = new Map<string, Suggestion>();

  for (const { label, kind } of candidates) {
    if (!label) continue;
    const norm = normalizeText(label);
    if (!norm || norm === needle) continue;
    // Substring matches są filtrowane wyżej (wynik byłby zwrócony przez wyszukiwarkę).

    const words = norm.split(/\s+/);
    let bestDist = Infinity;
    let bestWordStartsSame = false;
    for (const w of words) {
      const startsSame = w[0] === needle[0];
      const dWhole = levenshtein(needle, w);
      if (dWhole < bestDist) { bestDist = dWhole; bestWordStartsSame = startsSame; }
      if (w.length > needle.length) {
        const dPref = levenshtein(needle, w.slice(0, needle.length));
        if (dPref < bestDist) { bestDist = dPref; bestWordStartsSame = startsSame; }
        const dPref1 = levenshtein(needle, w.slice(0, needle.length + 1));
        if (dPref1 < bestDist) { bestDist = dPref1; bestWordStartsSame = startsSame; }
      }
    }

    // Heurystyka anty-fałszywka: pierwsza litera musi się zgadzać z którymś
    // słowem w nazwie. Bez tego "warz" potrafi sugerować "Tarnów" itp.
    if (!bestWordStartsSame) continue;

    const maxDist = needle.length <= 4 ? 1 : needle.length <= 7 ? 2 : 3;
    if (bestDist <= maxDist) {
      const existing = best.get(norm);
      if (!existing || bestDist < existing.distance) {
        best.set(norm, { label, kind, distance: bestDist });
      }
    }
  }

  return [...best.values()]
    .sort((a, b) => a.distance - b.distance)
    .slice(0, limit);
};
